import { useState, useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Select from "@/v2-ui/select"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import FeatureFlagCard from "@/v2-backoffice-client/featureFlags/FeatureFlagCard"
import {
  MOCK_FEATURE_FLAGS,
  type FeatureFlag,
} from "@/v2-backoffice-client/featureFlags/featureFlags.mock"

export type FeatureFlagsPageProps = PageProps

function FeatureFlagsPage(props: FeatureFlagsPageProps) {
  const { className, ...pageProps } = props

  const [flags, setFlags] = useState<FeatureFlag[]>(MOCK_FEATURE_FLAGS)
  const [search, setSearch] = useState("")
  const [scopeFilter, setScopeFilter] = useState("all")

  const filteredFlags = useMemo(() => {
    return flags.filter((flag) => {
      const matchesSearch =
        !search ||
        flag.name.toLowerCase().includes(search.toLowerCase()) ||
        flag.id.toLowerCase().includes(search.toLowerCase()) ||
        flag.description.toLowerCase().includes(search.toLowerCase())

      const matchesScope = scopeFilter === "all" || flag.scope === scopeFilter

      return matchesSearch && matchesScope
    })
  }, [flags, search, scopeFilter])

  const enabledCount = flags.filter((f) => f.isEnabled).length

  function handleToggle(id: string, enabled: boolean) {
    setFlags((prev) =>
      prev.map((f) =>
        f.id === id
          ? { ...f, isEnabled: enabled, updatedAt: new Date().toISOString() }
          : f,
      ),
    )
  }

  return (
    <Page className={cn("feature-flags-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title>Feature Flags</Page.Title>
        <Page.Header.Actions>
          <span className="text-sm text-muted-foreground">
            {enabledCount} of {flags.length} enabled
          </span>
        </Page.Header.Actions>
      </Page.Header>

      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <Input
          type="search"
          placeholder="Search flags..."
          className="w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Select value={scopeFilter} onValueChange={setScopeFilter}>
          <Select.Trigger size="sm" className="w-36">
            <Select.Value placeholder="Scope" />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="all">All scopes</Select.Item>
            <Select.Item value="global">Global</Select.Item>
            <Select.Item value="tenant">Team</Select.Item>
            <Select.Item value="user">User</Select.Item>
          </Select.Content>
        </Select>
      </div>

      <Page.Content>
        <div className="feature-flags-list">
          {filteredFlags.map((flag) => (
            <FeatureFlagCard
              key={flag.id}
              flag={flag}
              onToggle={handleToggle}
            />
          ))}

          {filteredFlags.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No feature flags match your search.
            </p>
          )}
        </div>
      </Page.Content>
    </Page>
  )
}

export default FeatureFlagsPage
